import React, { useState, useEffect, useCallback } from 'react';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Grid,
  Divider,
  FormControlLabel,
  InputAdornment,
} from '@mui/material';
import TogglePill from '../components/TogglePill';
import { useNavigate } from 'react-router-dom';
import { AppIconButton } from '../components/ui';
import api from '../api/api';
import { Business, Store, Upload } from '@mui/icons-material';
import { useTheme } from '../context/ThemeContext';
import { useConfirm } from '../context/ConfirmContext';
import MenuFileUpload from '../components/MenuFileUpload';

interface VendorOnboardingForm {
  username: string;
  email: string;
  password: string;
  businessName: string;
  phoneNumber: string;
  address: string;
  businessType: string;
  country: string;
  defaultOrderType: string;
  defaultPaymentMode: string;
  serviceChargePercentage: number;
  gstPercentage: number;
  packagingChargePerOrder: number;
  createTables: boolean;
  numberOfTables: number;
}

interface ProgressStep {
  step: string;
  status: 'pending' | 'in_progress' | 'completed' | 'error';
  message: string;
  timestamp?: Date;
}

const AdminVendorOnboarding: React.FC = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [menuFile, setMenuFile] = useState<File | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [menuExtractionStatus, setMenuExtractionStatus] = useState<{
    extracting: boolean;
    success: boolean | null;
    categories: number;
    items: number;
    message: string;
  }>({ extracting: false, success: null, categories: 0, items: 0, message: '' });

  // Progress tracking
  const [progressSteps, setProgressSteps] = useState<ProgressStep[]>([]);
  const [showProgress, setShowProgress] = useState(false);

  // Real-time validation
  const [usernameAvailable, setUsernameAvailable] = useState<boolean | null>(null);
  const [emailAvailable, setEmailAvailable] = useState<boolean | null>(null);
  const [similarVendors, setSimilarVendors] = useState<Array<{ id: number; businessName: string; username: string }>>([]);
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [checkingBusinessName, setCheckingBusinessName] = useState(false);

  // Field-specific errors
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const [formData, setFormData] = useState<VendorOnboardingForm>({
    username: '',
    email: '',
    password: 'password123', // Default password
    businessName: '',
    phoneNumber: '',
    address: '',
    businessType: 'RESTAURANT',
    country: 'IN',
    defaultOrderType: 'DINE_IN',
    defaultPaymentMode: 'CASH',
    serviceChargePercentage: 0,
    gstPercentage: 5,
    packagingChargePerOrder: 0,
    createTables: true,
    numberOfTables: 10,
  });

  const businessTypes = [
    'RESTAURANT',
    'CAFE',
    'QSR',
    'FOOD_TRUCK',
    'CLOUD_KITCHEN',
    'BAR_AND_RESTAURANT',
  ];

  const orderTypes = ['DINE_IN', 'TAKEAWAY', 'DELIVERY'];
  const paymentModes = ['CASH', 'CARD', 'UPI', 'WALLET'];

  // Real-time validation with debounce
  useEffect(() => {
    const checkUsername = setTimeout(() => {
      if (formData.username && formData.username.length >= 3) {
        setCheckingUsername(true);
        api.get(`/api/admin/check-username?username=${formData.username}`)
          .then(res => {
            setUsernameAvailable(res.data.available);
          })
          .catch(() => setUsernameAvailable(null))
          .finally(() => setCheckingUsername(false));
      } else {
        setUsernameAvailable(null);
      }
    }, 500);

    return () => clearTimeout(checkUsername);
  }, [formData.username]);

  useEffect(() => {
    const checkEmail = setTimeout(() => {
      if (formData.email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        setCheckingEmail(true);
        api.get(`/api/admin/check-email?email=${formData.email}`)
          .then(res => {
            setEmailAvailable(res.data.available);
          })
          .catch(() => setEmailAvailable(null))
          .finally(() => setCheckingEmail(false));
      } else {
        setEmailAvailable(null);
      }
    }, 500);

    return () => clearTimeout(checkEmail);
  }, [formData.email]);

  useEffect(() => {
    const checkBusinessName = setTimeout(() => {
      if (formData.businessName && formData.businessName.length >= 3) {
        setCheckingBusinessName(true);
        api.get(`/api/admin/check-similar-vendors?businessName=${encodeURIComponent(formData.businessName)}`)
          .then(res => {
            setSimilarVendors(res.data.similarVendors || []);
          })
          .catch(() => setSimilarVendors([]))
          .finally(() => setCheckingBusinessName(false));
      } else {
        setSimilarVendors([]);
      }
    }, 500);

    return () => clearTimeout(checkBusinessName);
  }, [formData.businessName]);

  // Progress tracking helpers
  const updateProgress = (step: string, status: ProgressStep['status'], message: string) => {
    setProgressSteps(prev => {
      const existingIndex = prev.findIndex(s => s.step === step);
      const newStep: ProgressStep = { step, status, message, timestamp: new Date() };

      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex] = newStep;
        return updated;
      }
      return [...prev, newStep];
    });
  };

  const initializeProgress = () => {
    const steps: ProgressStep[] = [
      { step: 'validate', status: 'pending', message: 'Validating form data...' },
      { step: 'create_vendor', status: 'pending', message: 'Creating vendor account...' },
    ];

    if (menuFile) {
      // Show extraction summary if available
      const extractionSummary = menuExtractionStatus.success
        ? ` (${menuExtractionStatus.categories} categories, ${menuExtractionStatus.items} items)`
        : '';
      steps.push(
        { step: 'upload_menu', status: 'pending', message: 'Uploading extracted menu data...' },
        { step: 'process_categories', status: 'pending', message: `Processing categories${extractionSummary}...` },
        { step: 'process_items', status: 'pending', message: 'Creating menu items...' }
      );
    }

    if (formData.createTables) {
      steps.push({ step: 'create_tables', status: 'pending', message: `Creating ${formData.numberOfTables} tables...` });
    }

    steps.push({ step: 'finalize', status: 'pending', message: 'Finalizing setup...' });

    setProgressSteps(steps);
    setShowProgress(true);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));

    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSelectChange = (name: string) => (event: any) => {
    setFormData(prev => ({
      ...prev,
      [name]: event.target.value,
    }));
  };

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      createTables: e.target.checked,
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setMenuFile(e.target.files[0]);
    }
  };

  // Client-side validation
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    // Required fields
    if (!formData.username.trim()) {
      errors.username = 'Username is required';
    } else if (formData.username.length < 3) {
      errors.username = 'Username must be at least 3 characters';
    }

    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (!formData.password.trim()) {
      errors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }

    if (!formData.businessName.trim()) {
      errors.businessName = 'Business name is required';
    }

    // Phone number validation — branches by the selected country (UK plan Phase 5)
    if (formData.phoneNumber) {
      const digitsOnly = formData.phoneNumber.replace(/\D/g, ''); // Remove non-digits
      if (formData.country === 'GB') {
        // UK mobile: 07XXXXXXXXX (11 digits) or 447XXXXXXXXX (+44 form)
        if (!/^(07\d{9}|447\d{9})$/.test(digitsOnly)) {
          errors.phoneNumber = 'Please enter a valid UK mobile number (07… or +44 7…)';
        }
      } else if (digitsOnly.length !== 10) {
        errors.phoneNumber = 'Phone number must be exactly 10 digits';
      } else if (!/^[6-9]\d{9}$/.test(digitsOnly)) {
        errors.phoneNumber = 'Please enter a valid Indian mobile number (starting with 6-9)';
      }
    }

    // Table validation
    if (formData.createTables && formData.numberOfTables < 1) {
      errors.numberOfTables = 'Number of tables must be at least 1';
    }

    setFieldErrors(errors);

    // Scroll to first error
    if (Object.keys(errors).length > 0) {
      const firstErrorField = Object.keys(errors)[0];
      const element = document.getElementsByName(firstErrorField)[0];
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.focus();
      }
    }

    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setFieldErrors({});
    setShowProgress(false);

    // Block submission if menu extraction is in progress
    if (menuExtractionStatus.extracting) {
      setError('Please wait for menu extraction to complete before submitting.');
      return;
    }

    // Block submission if menu extraction failed but file is still attached
    if (menuExtractionStatus.success === false && menuFile) {
      setError('Menu extraction failed. Please fix the menu files or remove them before submitting.');
      return;
    }

    // Validate form before submission
    updateProgress('validate', 'in_progress', 'Validating form data...');
    if (!validateForm()) {
      setError('Please fix the errors below before submitting');
      updateProgress('validate', 'error', 'Validation failed');
      return;
    }
    updateProgress('validate', 'completed', 'Validation successful ✓');

    setLoading(true);
    initializeProgress();

    console.log('Form submission - menuFile:', menuFile ? `${menuFile.name} (${menuFile.size} bytes)` : 'No menu file');

    try {
      let response;

      updateProgress('create_vendor', 'in_progress', 'Creating vendor account...');

      if (menuFile) {
        console.log('Submitting with menu file:', menuFile.name);
        updateProgress('upload_menu', 'in_progress', `Uploading ${menuFile.name}...`);

        // Submit with menu file
        const formDataToSend = new FormData();
        formDataToSend.append('vendorData', new Blob([JSON.stringify(formData)], {
          type: 'application/json'
        }));
        formDataToSend.append('menuFile', menuFile);

        // Simulate progress updates (since we can't track actual upload progress easily)
        const progressTimer = setInterval(() => {
          updateProgress('upload_menu', 'in_progress', 'Processing menu file...');
        }, 2000);

        response = await api.post('/api/admin/vendor-onboarding/with-menu-file', formDataToSend, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        clearInterval(progressTimer);
        updateProgress('upload_menu', 'completed', 'Menu file uploaded ✓');
        updateProgress('extract_menu', 'completed', 'Menu data extracted ✓');
      } else {
        // Submit without menu file
        response = await api.post('/api/admin/vendor-onboarding', formData);
      }

      updateProgress('create_vendor', 'completed', 'Vendor account created ✓');

      // If we got here without error, it means 2xx status (success)
      // Check multiple possible response formats from backend
      const isSuccess = response.status >= 200 && response.status < 300;

      if (isSuccess) {
        const responseData = response.data || {};

        // Update progress for menu processing
        if (menuFile && responseData.menuItems) {
          updateProgress('process_categories', 'completed', `Processed ${responseData.categories?.length || 0} categories ✓`);
          updateProgress('process_items', 'completed', `Created ${responseData.menuItems.length} menu items ✓`);
        }

        // Update progress for table creation
        if (formData.createTables) {
          updateProgress('create_tables', 'completed', `Created ${formData.numberOfTables} tables ✓`);
        }

        updateProgress('finalize', 'completed', 'Setup completed successfully! ✓');

        let successMessage = `Vendor "${formData.businessName}" onboarded successfully!`;

        // Add menu processing details if available
        if (responseData.menuItems && responseData.menuItems.length > 0) {
          const categoryCount = responseData.categories ? responseData.categories.length : 0;
          const itemCount = responseData.menuItems.length;
          successMessage += ` Menu processed with ${categoryCount} categories and ${itemCount} items.`;
        }

        // If backend provides a message, append it
        if (responseData.message) {
          successMessage = responseData.message;
        }

        setSuccess(successMessage);
        setFieldErrors({}); // Clear any field errors

        // Reset form
        setFormData({
          username: '',
          email: '',
          password: 'password123',
          businessName: '',
          phoneNumber: '',
          address: '',
          businessType: 'RESTAURANT',
          defaultOrderType: 'DINE_IN',
          defaultPaymentMode: 'CASH',
          serviceChargePercentage: 0,
          gstPercentage: 5,
          packagingChargePerOrder: 0,
          createTables: true,
          numberOfTables: 10,
          country: 'IN',
        });
        setMenuFile(null);

        // Scroll to top to show success message
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        // This shouldn't happen since axios throws on non-2xx, but just in case
        setError(response.data?.message || 'Failed to onboard vendor');
      }
    } catch (err: any) {
      console.error('Onboarding error:', err);

      // Parse backend validation errors
      if (err.response?.status === 400 && err.response?.data) {
        const backendErrors = err.response.data.errors || {};
        const backendMessage = err.response.data.message;

        // If there are field-specific errors, parse them
        if (Object.keys(backendErrors).length > 0) {
          const parsedErrors: Record<string, string> = {};

          Object.keys(backendErrors).forEach((field) => {
            const errorValue = backendErrors[field];
            // Handle both array of errors and single error string
            if (Array.isArray(errorValue)) {
              parsedErrors[field] = errorValue[0];
            } else if (typeof errorValue === 'string') {
              parsedErrors[field] = errorValue;
            }
          });

          setFieldErrors(parsedErrors);
          setError(backendMessage || 'Please fix the validation errors below');

          // Scroll to first error field
          const firstErrorField = Object.keys(parsedErrors)[0];
          const element = document.getElementsByName(firstErrorField)[0];
          if (element) {
            setTimeout(() => {
              element.scrollIntoView({ behavior: 'smooth', block: 'center' });
              element.focus();
            }, 100);
          }
        } else {
          // Generic error message
          setError(backendMessage || 'Validation failed. Please check your inputs.');
        }
      } else if (err.response?.status === 409) {
        // Conflict - username or email already exists
        setError(err.response?.data?.message || 'Username or email already exists');
      } else {
        // Other errors
        setError(err.response?.data?.message || 'An error occurred during onboarding. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const applyTemplate = async (templateName: string) => {
    setLoading(true);
    try {
      // Update form based on template
      switch (templateName) {
        case 'QSR':
        case 'FOOD_TRUCK':
          setFormData(prev => ({
            ...prev,
            businessType: templateName,
            defaultOrderType: 'TAKEAWAY',
            createTables: false,
            numberOfTables: 0,
          }));
          break;
        case 'CLOUD_KITCHEN':
          setFormData(prev => ({
            ...prev,
            businessType: templateName,
            defaultOrderType: 'DELIVERY',
            createTables: false,
            numberOfTables: 0,
          }));
          break;
        case 'CAFE':
          setFormData(prev => ({
            ...prev,
            businessType: templateName,
            defaultOrderType: 'DINE_IN',
            createTables: true,
            numberOfTables: 15,
          }));
          break;
        case 'BAR_AND_RESTAURANT':
          setFormData(prev => ({
            ...prev,
            businessType: templateName,
            defaultOrderType: 'DINE_IN',
            createTables: true,
            numberOfTables: 25,
          }));
          break;
        default:
          setFormData(prev => ({
            ...prev,
            businessType: 'RESTAURANT',
            defaultOrderType: 'DINE_IN',
            createTables: true,
            numberOfTables: 20,
          }));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box display="flex" alignItems="center" mb={3}>
          <Business sx={{ mr: 2, fontSize: 40, color: 'primary.main' }} />
          <Typography variant="h4" component="h1">
            Vendor Onboarding
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            onClose={() => {
              setSuccess(null);
              setShowProgress(false);
            }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => navigate('/admin/users')}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.2)'
                  }
                }}
              >
                View All Vendors →
              </Button>
            }
          >
            {success}
          </Alert>
        )}

        {/* Progress Tracker */}
        {showProgress && progressSteps.length > 0 && (
          <Paper
            elevation={2}
            sx={{
              mb: 3,
              p: 3,
              backgroundColor: theme.gray[50] || '#f8fafc',
              border: `1px solid ${theme.components?.card?.border || '#e2e8f0'}`
            }}
          >
            <Typography variant="h6" gutterBottom sx={{ mb: 2, color: theme.text || '#1e293b', fontWeight: 600 }}>
              Onboarding Progress
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {progressSteps.map((step, index) => (
                <Box
                  key={step.step}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    padding: '12px 16px',
                    borderRadius: '8px',
                    backgroundColor: theme.components?.card?.background || 'white',
                    border: '1px solid',
                    borderColor: step.status === 'completed' ? (theme.success || '#10b981') :
                      step.status === 'in_progress' ? (theme.primary || '#3b82f6') :
                        step.status === 'error' ? (theme.danger || '#ef4444') : (theme.gray[300] || '#cbd5e1'),
                    transition: 'all 0.3s ease'
                  }}
                >
                  {/* Status Icon */}
                  <Box sx={{ flexShrink: 0, width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {step.status === 'completed' && (
                      <svg className="w-6 h-6" fill="none" stroke={theme.success || '#10b981'} viewBox="0 0 24 24" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                    {step.status === 'in_progress' && (
                      <CircularProgress size={20} sx={{ color: theme.primary || '#3b82f6' }} />
                    )}
                    {step.status === 'error' && (
                      <svg className="w-6 h-6" fill="none" stroke={theme.danger || '#ef4444'} viewBox="0 0 24 24" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    )}
                    {step.status === 'pending' && (
                      <Box sx={{ width: 20, height: 20, borderRadius: '50%', border: `2px solid ${theme.gray[300] || '#cbd5e1'}` }} />
                    )}
                  </Box>

                  {/* Step Message */}
                  <Typography
                    sx={{
                      flex: 1,
                      fontWeight: step.status === 'in_progress' ? 600 : 500,
                      color: step.status === 'completed' ? (theme.success || '#10b981') :
                        step.status === 'in_progress' ? (theme.text || '#1e293b') :
                          step.status === 'error' ? (theme.danger || '#ef4444') : (theme.gray[500] || '#64748b'),
                      fontSize: '0.95rem'
                    }}
                  >
                    {step.message}
                  </Typography>

                  {/* Timestamp (for completed steps) */}
                  {step.timestamp && step.status === 'completed' && (
                    <Typography
                      variant="caption"
                      sx={{ color: theme.gray[500] || '#64748b', fontSize: '0.75rem' }}
                    >
                      {step.timestamp.toLocaleTimeString()}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          </Paper>
        )}

        {/* Enhanced Quick Templates Section */}
        <Paper
          elevation={0}
          sx={{
            mb: 4,
            p: 3,
            backgroundColor: theme.gray[50] || '#f8fafc',
            border: `1px solid ${theme.components?.card?.border || '#e2e8f0'}`,
            borderRadius: '12px',
          }}
        >
          <Box display="flex" alignItems="center" gap={1.5} mb={2.5}>
            <Store sx={{ fontSize: 28, color: theme.primary || '#3b82f6' }} />
            <Typography variant="h6" fontWeight={700} sx={{ color: theme.text || '#1e293b' }}>
              Quick Templates
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ color: theme.gray[500] || '#64748b', mb: 3 }}>
            Choose a business type to auto-fill recommended settings
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
              },
              gap: 2,
            }}
          >
            <Button
              variant="contained"
              onClick={() => applyTemplate('RESTAURANT')}
              disabled={loading}
              startIcon={<span style={{ fontSize: '1.25rem' }}>🍽️</span>}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                py: 1.5,
                backgroundColor: `${theme.primary || '#2563eb'}20 !important`,
                color: `${theme.primary || '#2563eb'} !important`,
                borderRadius: '10px',
                border: `1px solid ${theme.primary || '#2563eb'}50`,
                boxShadow: 'none !important',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: `${theme.primary || '#2563eb'}30 !important`,
                  transform: 'translateY(-2px)',
                  boxShadow: `0 4px 12px ${theme.primary || '#2563eb'}33 !important`,
                },
                '&:active': {
                  transform: 'translateY(0)',
                }
              }}
            >
              Restaurant
            </Button>

            <Button
              variant="contained"
              onClick={() => applyTemplate('CAFE')}
              disabled={loading}
              startIcon={<span style={{ fontSize: '1.25rem' }}>☕</span>}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                py: 1.5,
                backgroundColor: 'rgba(139, 92, 246, 0.2) !important',
                color: 'var(--accent-alt) !important',
                borderRadius: '10px',
                border: '1px solid rgba(139, 92, 246, 0.31)',
                boxShadow: 'none !important',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: 'rgba(139, 92, 246, 0.3) !important',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(139, 92, 246, 0.2) !important',
                },
                '&:active': {
                  transform: 'translateY(0)',
                }
              }}
            >
              Cafe
            </Button>

            <Button
              variant="contained"
              onClick={() => applyTemplate('QSR')}
              disabled={loading}
              startIcon={<span style={{ fontSize: '1.25rem' }}>🍔</span>}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                py: 1.5,
                backgroundColor: `${theme.success || '#10B981'}20 !important`,
                color: `${theme.success || '#10B981'} !important`,
                borderRadius: '10px',
                border: `1px solid ${theme.success || '#10B981'}50`,
                boxShadow: 'none !important',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: `${theme.success || '#10B981'}30 !important`,
                  transform: 'translateY(-2px)',
                  boxShadow: `0 4px 12px ${theme.success || '#10B981'}33 !important`,
                },
                '&:active': {
                  transform: 'translateY(0)',
                }
              }}
            >
              QSR
            </Button>

            <Button
              variant="contained"
              onClick={() => applyTemplate('FOOD_TRUCK')}
              disabled={loading}
              startIcon={<span style={{ fontSize: '1.25rem' }}>🚚</span>}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                py: 1.5,
                backgroundColor: `${theme.warning || '#d97706'}20 !important`,
                color: `${theme.warning || '#d97706'} !important`,
                borderRadius: '10px',
                border: `1px solid ${theme.warning || '#d97706'}50`,
                boxShadow: 'none !important',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: `${theme.warning || '#d97706'}30 !important`,
                  transform: 'translateY(-2px)',
                  boxShadow: `0 4px 12px ${theme.warning || '#d97706'}33 !important`,
                },
                '&:active': {
                  transform: 'translateY(0)',
                }
              }}
            >
              Food Truck
            </Button>

            <Button
              variant="contained"
              onClick={() => applyTemplate('CLOUD_KITCHEN')}
              disabled={loading}
              startIcon={<span style={{ fontSize: '1.25rem' }}>👨‍🍳</span>}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                py: 1.5,
                backgroundColor: 'rgba(6, 182, 212, 0.2) !important',
                color: '#0891b2 !important', /* basil-todo */
                borderRadius: '10px',
                border: '1px solid rgba(6, 182, 212, 0.31)',
                boxShadow: 'none !important',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: 'rgba(6, 182, 212, 0.3) !important',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(6, 182, 212, 0.2) !important',
                },
                '&:active': {
                  transform: 'translateY(0)',
                }
              }}
            >
              Cloud Kitchen
            </Button>

            <Button
              variant="contained"
              onClick={() => applyTemplate('BAR_AND_RESTAURANT')}
              disabled={loading}
              startIcon={<span style={{ fontSize: '1.25rem' }}>🍷</span>}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                py: 1.5,
                backgroundColor: `${theme.danger || '#EF4444'}20 !important`,
                color: `${theme.danger || '#EF4444'} !important`,
                borderRadius: '10px',
                border: `1px solid ${theme.danger || '#EF4444'}50`,
                boxShadow: 'none !important',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: `${theme.danger || '#EF4444'}30 !important`,
                  transform: 'translateY(-2px)',
                  boxShadow: `0 4px 12px ${theme.danger || '#EF4444'}33 !important`,
                },
                '&:active': {
                  transform: 'translateY(0)',
                }
              }}
            >
              Bar & Restaurant
            </Button>
          </Box>
        </Paper>

        <Divider sx={{ mb: 3 }} />

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Username"
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                required
                disabled={loading}
                error={!!fieldErrors.username || (usernameAvailable === false)}
                helperText={
                  fieldErrors.username ||
                  (checkingUsername ? 'Checking availability...' :
                    usernameAvailable === false ? '⚠️ Username already taken' :
                      usernameAvailable === true ? '✓ Username available' :
                        'Unique username for login (min 3 characters)')
                }
                InputProps={{
                  endAdornment: checkingUsername ? (
                    <CircularProgress size={20} />
                  ) : usernameAvailable === true ? (
                    <span style={{ color: theme.success || '#10b981', fontSize: '1.2rem' }}>✓</span>
                  ) : usernameAvailable === false ? (
                    <span style={{ color: theme.danger || '#ef4444', fontSize: '1.2rem' }}>✕</span>
                  ) : null
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&.Mui-focused fieldset': {
                      borderColor: usernameAvailable === true ? (theme.success || '#10b981') :
                        usernameAvailable === false ? (theme.danger || '#ef4444') : undefined
                    }
                  },
                  '& .MuiFormHelperText-root': {
                    color: usernameAvailable === true ? (theme.success || '#10b981') :
                      usernameAvailable === false ? (theme.danger || '#ef4444') : undefined
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                disabled={loading}
                error={!!fieldErrors.email || (emailAvailable === false)}
                helperText={
                  fieldErrors.email ||
                  (checkingEmail ? 'Checking availability...' :
                    emailAvailable === false ? '⚠️ Email already registered' :
                      emailAvailable === true ? '✓ Email available' :
                        'Valid email address for notifications')
                }
                InputProps={{
                  endAdornment: checkingEmail ? (
                    <CircularProgress size={20} />
                  ) : emailAvailable === true ? (
                    <span style={{ color: theme.success || '#10b981', fontSize: '1.2rem' }}>✓</span>
                  ) : emailAvailable === false ? (
                    <span style={{ color: theme.danger || '#ef4444', fontSize: '1.2rem' }}>✕</span>
                  ) : null
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&.Mui-focused fieldset': {
                      borderColor: emailAvailable === true ? (theme.success || '#10b981') :
                        emailAvailable === false ? (theme.danger || '#ef4444') : undefined
                    }
                  },
                  '& .MuiFormHelperText-root': {
                    color: emailAvailable === true ? (theme.success || '#10b981') :
                      emailAvailable === false ? (theme.danger || '#ef4444') : undefined
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleInputChange}
                required
                disabled={loading}
                error={!!fieldErrors.password}
                helperText={fieldErrors.password || 'Default: password123 (min 6 characters)'}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <AppIconButton
                        icon={showPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                        tooltip={showPassword ? 'Hide password' : 'Show password'}
                        variant="primary"
                        edge="end"
                        tabIndex={-1}
                        sx={{
                          width: 40,
                          height: 40,
                          padding: '10px',
                          border: 'none',
                          boxShadow: 'none',
                        }}
                        onClick={() => setShowPassword(v => !v)}
                      />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ position: 'relative' }}>
                <TextField
                  fullWidth
                  label="Business Name"
                  name="businessName"
                  value={formData.businessName}
                  onChange={handleInputChange}
                  required
                  disabled={loading}
                  error={!!fieldErrors.businessName}
                  helperText={
                    fieldErrors.businessName ||
                    (checkingBusinessName ? 'Checking for similar vendors...' :
                      similarVendors.length > 0 ? `⚠️ Found ${similarVendors.length} similar vendor(s)` :
                        'Display name for the business')
                  }
                  InputProps={{
                    endAdornment: checkingBusinessName ? (
                      <CircularProgress size={20} />
                    ) : similarVendors.length > 0 ? (
                      <span style={{ color: theme.warning || '#f59e0b', fontSize: '1.2rem' }}>⚠</span>
                    ) : null
                  }}
                  sx={{
                    '& .MuiFormHelperText-root': {
                      color: similarVendors.length > 0 ? (theme.warning || '#f59e0b') : undefined
                    }
                  }}
                />

                {/* Similar Vendors Suggestion Box */}
                {similarVendors.length > 0 && !loading && (
                  <Paper
                    elevation={4}
                    sx={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      zIndex: 1000,
                      mt: 1,
                      maxHeight: '200px',
                      overflow: 'auto',
                      backgroundColor: `${theme.warning || '#f59e0b'}10`,
                      border: `2px solid ${theme.warning || '#f59e0b'}`
                    }}
                  >
                    <Box sx={{ p: 2 }}>
                      <Typography variant="subtitle2" sx={{ color: theme.warning || '#d97706', fontWeight: 600, mb: 1 }}>
                        ⚠️ Similar vendors found - Please verify:
                      </Typography>
                      {similarVendors.map((vendor) => (
                        <Box
                          key={vendor.id}
                          sx={{
                            p: 1.5,
                            mb: 1,
                            backgroundColor: theme.components?.card?.background || 'white',
                            borderRadius: '6px',
                            border: `1px solid ${theme.warning || '#fcd34d'}`,
                            cursor: 'pointer',
                            '&:hover': {
                              backgroundColor: `${theme.warning || '#fcd34d'}15`
                            }
                          }}
                          onClick={async () => {
                            // Navigate to vendor detail or show more info
                            if (await confirm({
                              title: 'Vendor already exists',
                              description: `This vendor "${vendor.businessName}" (username: ${vendor.username}) already exists. Do you want to view it?`,
                              confirmText: 'View vendor',
                              cancelText: 'Cancel',
                            })) {
                              navigate(`/admin/users`);
                            }
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 600, color: theme.text || '#1e293b' }}>
                            {vendor.businessName}
                          </Typography>
                          <Typography variant="caption" sx={{ color: theme.gray[500] || '#64748b' }}>
                            Username: {vendor.username}
                          </Typography>
                        </Box>
                      ))}
                      <Typography variant="caption" sx={{ color: theme.warning || '#92400e', mt: 1, display: 'block' }}>
                        Click on a vendor to view details, or continue if this is a different business.
                      </Typography>
                    </Box>
                  </Paper>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Phone Number"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleInputChange}
                disabled={loading}
                error={!!fieldErrors.phoneNumber}
                helperText={fieldErrors.phoneNumber || '10-digit mobile number (e.g., 9876543210)'}
                placeholder="9876543210"
                inputProps={{
                  maxLength: 10,
                  pattern: '[0-9]*',
                  inputMode: 'numeric'
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Address"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                disabled={loading}
                error={!!fieldErrors.address}
                helperText={fieldErrors.address}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }}>
                <Typography variant="subtitle2" color="textSecondary">
                  Business Configuration
                </Typography>
              </Divider>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel id="businessType-label" sx={{ textDecoration: 'none' }}>Business Type</InputLabel>
                <Select
                  labelId="businessType-label"
                  label="Business Type"
                  value={formData.businessType}
                  onChange={handleSelectChange('businessType')}
                  disabled={loading}
                >
                  {businessTypes.map(type => (
                    <MenuItem key={type} value={type}>
                      {type.replace(/_/g, ' ')}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel id="country-label" sx={{ textDecoration: 'none' }}>Country</InputLabel>
                <Select
                  labelId="country-label"
                  label="Country"
                  value={formData.country}
                  onChange={handleSelectChange('country')}
                  disabled={loading}
                >
                  <MenuItem value="IN">India (₹ INR — GST)</MenuItem>
                  <MenuItem value="GB">United Kingdom (£ GBP — VAT)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel id="defaultOrderType-label" sx={{ textDecoration: 'none' }}>Default Order Type</InputLabel>
                <Select
                  labelId="defaultOrderType-label"
                  label="Default Order Type"
                  value={formData.defaultOrderType}
                  onChange={handleSelectChange('defaultOrderType')}
                  disabled={loading}
                >
                  {orderTypes.map(type => (
                    <MenuItem key={type} value={type}>
                      {type.replace(/_/g, ' ')}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel id="defaultPaymentMode-label" sx={{ textDecoration: 'none' }}>Default Payment Mode</InputLabel>
                <Select
                  labelId="defaultPaymentMode-label"
                  label="Default Payment Mode"
                  value={formData.defaultPaymentMode}
                  onChange={handleSelectChange('defaultPaymentMode')}
                  disabled={loading}
                >
                  {paymentModes.map(mode => (
                    <MenuItem key={mode} value={mode}>
                      {mode}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Service Charge"
                name="serviceChargePercentage"
                type="number"
                value={formData.serviceChargePercentage}
                onChange={handleInputChange}
                disabled={loading}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="GST"
                name="gstPercentage"
                type="number"
                value={formData.gstPercentage}
                onChange={handleInputChange}
                disabled={loading}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Packaging Charge"
                name="packagingChargePerOrder"
                type="number"
                value={formData.packagingChargePerOrder}
                onChange={handleInputChange}
                disabled={loading}
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }}>
                <Typography variant="subtitle2" color="textSecondary">
                  Table Configuration
                </Typography>
              </Divider>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                height: '100%',
                pt: { xs: 1, md: 0 }
              }}>
                <FormControlLabel
                  control={
                    <TogglePill
                      checked={formData.createTables}
                      onChange={(v) => setFormData(prev => ({ ...prev, createTables: v }))}
                      disabled={loading}
                    />
                  }
                  label="Create Tables"
                  sx={{
                    m: 0,
                    gap: 1.5,
                    '& .MuiFormControlLabel-label': {
                      fontSize: { xs: '0.95rem', sm: '1rem' },
                      fontWeight: 500
                    }
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Number of Tables"
                name="numberOfTables"
                type="number"
                value={formData.numberOfTables}
                onChange={handleInputChange}
                disabled={loading || !formData.createTables}
                error={!!fieldErrors.numberOfTables}
                helperText={fieldErrors.numberOfTables || 'Number of tables to create (min 1)'}
                InputProps={{
                  inputProps: { min: 1, max: 999 }
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }}>
                <Typography variant="subtitle2" color="textSecondary">
                  Menu Data (Optional)
                </Typography>
              </Divider>
            </Grid>

            <Grid item xs={12}>
              <MenuFileUpload
                disabled={loading}
                onJsonFileSelected={(file) => {
                  console.log('Menu file selected/extracted:', file.name, 'Size:', file.size);
                  setMenuFile(file);
                }}
                onMenuDataExtracted={(data) => {
                  console.log('Menu data extracted with', data.categories?.length || 0, 'categories');
                }}
                onExtractionStart={() => {
                  setMenuExtractionStatus({
                    extracting: true,
                    success: null,
                    categories: 0,
                    items: 0,
                    message: 'Extracting menu data...'
                  });
                }}
                onExtractionComplete={(success, categories, items) => {
                  setMenuExtractionStatus({
                    extracting: false,
                    success,
                    categories,
                    items,
                    message: success
                      ? `Extracted ${categories} categories with ${items} items`
                      : 'Menu extraction failed - vendor will NOT be created with incomplete data'
                  });

                  // If extraction failed, clear the menu file to prevent submission
                  if (!success) {
                    setMenuFile(null);
                    setError('Menu extraction failed. Please fix the issues above or remove the menu files before proceeding.');
                  }
                }}
              />
              {/* Menu Extraction Status Warning */}
              {menuExtractionStatus.success === false && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Menu extraction failed. You have two options:
                  </Typography>
                  <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                    1. Fix the menu files and try again, OR<br/>
                    2. Remove the menu files and create the vendor without a menu (you can add menu items manually later)
                  </Typography>
                </Alert>
              )}
            </Grid>

            <Grid item xs={12}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm="auto">
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => navigate('/admin')}
                    disabled={loading}
                    sx={{
                      textTransform: 'none !important',
                      fontWeight: '600 !important',
                      px: 3,
                      py: 1.5,
                      backgroundColor: `${theme.components?.card?.background || '#ffffff'} !important`,
                      color: `${theme.gray[600] || '#4b5563'} !important`,
                      borderWidth: '1px !important',
                      borderStyle: 'solid !important',
                      borderColor: `${theme.components?.card?.border || '#6b7280'}50 !important`,
                      boxShadow: 'none !important',
                      '&:hover': {
                        backgroundColor: `${theme.components?.card?.hover || '#f3f4f6'} !important`,
                        borderColor: `${theme.components?.card?.border || '#6b7280'}66 !important`,
                        boxShadow: 'none !important',
                      }
                    }}
                  >
                    Cancel
                  </Button>
                </Grid>
                <Grid item xs={12} sm="auto">
                  <Button
                    fullWidth
                    type="submit"
                    variant="contained"
                    disabled={loading || menuExtractionStatus.extracting}
                    startIcon={loading ? <CircularProgress size={20} /> : menuExtractionStatus.extracting ? <CircularProgress size={20} /> : <Store />}
                    sx={{
                      textTransform: 'none !important',
                      fontWeight: '600 !important',
                      px: 3,
                      py: 1.5,
                      backgroundColor: (loading || menuExtractionStatus.extracting)
                        ? 'rgba(148, 163, 184, 0.2) !important'
                        : 'rgba(96, 165, 250, 0.2) !important',
                      color: (loading || menuExtractionStatus.extracting)
                        ? '#64748b !important'
                        : '#2563eb !important',
                      borderWidth: '1px !important',
                      borderStyle: 'solid !important',
                      borderColor: (loading || menuExtractionStatus.extracting)
                        ? 'rgba(148, 163, 184, 0.31) !important'
                        : 'rgba(96, 165, 250, 0.31) !important',
                      boxShadow: 'none !important',
                      '&:hover': {
                        backgroundColor: 'rgba(96, 165, 250, 0.25) !important',
                        borderColor: 'rgba(96, 165, 250, 0.4) !important',
                        boxShadow: 'none !important',
                      }
                    }}
                  >
                    {loading ? 'Onboarding...' : menuExtractionStatus.extracting ? 'Extracting Menu...' : 'Onboard Vendor'}
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Container>
  );
};

export default AdminVendorOnboarding;